import z from "zod/v4";
import { RealmId } from "./realm-id";
import { RealmIdSchema } from "./codecs";

/** Weighted realm for load distribution */
export const WeightedRealm = z.object({
  realm: RealmIdSchema,
  weight: z.number().positive(),
});
export type WeightedRealm = z.infer<typeof WeightedRealm>;

/** Non-empty array type for compile-time guarantees */
export type NonEmptyArray<T> = readonly [T, ...T[]];

/** Scope matcher for rule targeting */
export const ScopeMatcher = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("customer"), pattern: z.string() }),
  z.object({ kind: z.literal("branch"), pattern: z.string() }),
  z.object({ kind: z.literal("all") }),
]);
export type ScopeMatcher = z.infer<typeof ScopeMatcher>;

export const PinSolverRule = z.object({ kind: z.literal("pin"), match: ScopeMatcher, realm: RealmIdSchema });
export type PinSolverRule = z.infer<typeof PinSolverRule>;
export const RegionSolverRule = z.object({
  kind: z.literal("region"),
  match: ScopeMatcher,
  realms: z.array(WeightedRealm).nonempty(),
});
export type RegionSolverRule = z.infer<typeof RegionSolverRule>;

/** Solver rules - pin to specific realm or distribute across region */
export const SolverRule = z.discriminatedUnion("kind", [PinSolverRule, RegionSolverRule]);
export type SolverRule = z.infer<typeof SolverRule>;

/** Configuration for realm solver - guarantees totality via non-empty default */
export const RealmsolverConfig = z.object({
  rules: z.array(SolverRule).default([]),
  defaultRealms: z.array(WeightedRealm).nonempty(), // runtime non-empty guarantee
});
export type RealmsolverConfig = z.infer<typeof RealmsolverConfig>;

/** Serialize a RealmsolverConfig to JSON-compatible object (RealmId → string) */
const serializeConfig = (config: RealmsolverConfig): unknown => ({
  rules: config.rules.map((rule) => {
    if (rule.kind === "pin") {
      return { ...rule, realm: RealmId.toString(rule.realm) };
    }
    return {
      ...rule,
      realms: rule.realms.map((r) => ({ ...r, realm: RealmId.toString(r.realm) })),
    };
  }),
  defaultRealms: config.defaultRealms.map((r) => ({ ...r, realm: RealmId.toString(r.realm) })),
});

/** JSON codec for environment variable transport */
export const RealmsolverConfigJson = z.codec(
  z.string(),
  z.custom<RealmsolverConfig>(() => true),
  {
    decode: (jsonString, ctx) => {
      try {
        return RealmsolverConfig.parse(JSON.parse(jsonString));
      } catch (err: unknown) {
        ctx.issues.push({
          code: "invalid_format",
          format: "json",
          input: jsonString,
          message: err instanceof Error ? err.message : undefined,
        });
        return z.NEVER;
      }
    },
    encode: (value) => JSON.stringify(serializeConfig(value)),
  },
);
